/**
 * FRONT-1566: per-conversation role instructions. Opened from the header's
 * Role pill (`inbox-chat-modal.tsx`) for a chat only (`isChat` — a role has
 * no meaning on a document/file/feed-item reader). Everything typed here is
 * added to Eddy's context for THIS conversation only — see
 * `use-chat-role.ts`'s docblock for the full backend contract (blank/
 * whitespace clears the role; a 4000-char server cap; a voice call started
 * from this chat inherits it with no change to the voice mint payload).
 *
 * Modeled on `create-plan-dialog.tsx`'s three-part flex-column shape (pinned
 * header, an internally scrolling/filling body, pinned footer) so a long
 * draft or a validation message can never push Save/Cancel off-screen — but
 * wider AND taller (`sm:max-w-2xl`, a 320px-tall textarea) since this is
 * meant to feel like a real writing space, not a cramped one-line prompt.
 */

import { useEffect, useRef, useState } from 'react';
import { CircleNotchIcon, IdentificationBadgeIcon, MicrophoneIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/textarea';
import { Tip } from '~/components/ui/tip';
import { cn } from '~/lib/utils';
import { useChatRole, useSetChatRole, CHAT_ROLE_MAX_LENGTH } from '~/hooks/queries/use-chat-role';
import { useVoiceStt } from '~/hooks/use-voice-stt';
import { useSttModel } from '~/hooks/queries/use-stt-model';
import { RecordingBar } from '~/features/chat/chat-input';

export interface ChatRoleDialogProps {
  sessionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PLACEHOLDER =
  'You are my interview coach. Ask me one question at a time, push back on vague answers, and keep your replies under three sentences.';

export function ChatRoleDialog({ sessionId, open, onOpenChange }: ChatRoleDialogProps) {
  // Same query the header pill reads (`inbox-chat-modal.tsx`) — one cache
  // entry keyed by sessionId, so this dialog usually opens with the value
  // already warm instead of showing a fresh spinner.
  const { data, isPending, isError, error } = useChatRole(sessionId);
  const setRole = useSetChatRole(sessionId);

  const [draft, setDraft] = useState('');
  const [seeded, setSeeded] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const wasOpenRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset on the closed -> open edge only, so a reopen never shows a draft
  // abandoned on a previous open (Cancel/close discards it) — the seed
  // effect below re-derives the draft fresh from the persisted value every
  // time this flips true.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSeeded(false);
      setSaveError(null);
    }
    wasOpenRef.current = open;
  }, [open]);

  // Seed the draft with the loaded value once per open, the moment it's
  // actually available (immediately, if the query above was already warm —
  // typically true, since the header pill keeps it warm). Guarded by
  // `seeded` so a later background refetch of the same query can never
  // clobber what the user is mid-typing.
  useEffect(() => {
    if (open && !seeded && data !== undefined) {
      setDraft(data ?? '');
      setSeeded(true);
    }
  }, [open, seeded, data]);

  // One-shot dictation — identical wiring to chat-input.tsx's own mic
  // button (useSttModel for the shared per-user model + useVoiceStt),
  // appending each transcript pass rather than replacing the draft so
  // several dictation passes can build on each other.
  const sttModel = useSttModel();
  const voice = useVoiceStt({
    model: sttModel || undefined,
    onTranscript: (transcript) => {
      setDraft((prev) => (prev ? `${prev} ${transcript}` : transcript));
      requestAnimationFrame(() => textareaRef.current?.focus());
    },
  });
  const recording = voice.state === 'recording';
  const transcribing = voice.state === 'transcribing';

  // FRONT-1566 adversarial-review finding 2: the composer this dialog's
  // dictation was modelled on (chat-input.tsx) toasts a transcription
  // failure — this dialog silently dropped it, so denying the microphone
  // permission or a failed transcription just turned the spinner back into
  // a mic icon with no explanation and the user's speech gone.
  useEffect(() => {
    if (voice.error) toast.error(`Voice: ${voice.error}`);
  }, [voice.error]);

  // Live recording timer — same 250ms-tick shape as chat-input.tsx's own.
  const [recordingMs, setRecordingMs] = useState(0);
  useEffect(() => {
    if (voice.state !== 'recording') {
      setRecordingMs(0);
      return;
    }
    const startedAt = Date.now();
    setRecordingMs(0);
    const id = window.setInterval(() => setRecordingMs(Date.now() - startedAt), 250);
    return () => window.clearInterval(id);
  }, [voice.state]);

  const currentValue = data ?? '';
  const hasRole = currentValue.length > 0;
  const isDirty = draft !== currentValue;
  // FRONT-1566 adversarial-review finding 11: the route validates the
  // TRIMMED length (chat-threads-routes.ts's PUT handler), so gating on the
  // raw draft length here could refuse a save the server would accept —
  // e.g. a 4000-character role with a trailing newline. Match the route's
  // own check exactly.
  const overCap = draft.trim().length > CHAT_ROLE_MAX_LENGTH;
  const loaded = !isPending && !isError;
  const saving = setRole.isPending;

  function handleSave() {
    setSaveError(null);
    setRole.mutate(draft, {
      onSuccess: () => onOpenChange(false),
      onError: (err) => {
        setSaveError(
          err instanceof Error ? err.message : "Couldn't save this role. Try again.",
        );
      },
    });
  }

  function handleClear() {
    setSaveError(null);
    // Blank instructions CLEAR the role server-side (use-chat-role.ts's
    // docblock) — no confirm dialog, this is one field the user can retype.
    setRole.mutate('', {
      onSuccess: () => onOpenChange(false),
      onError: (err) => {
        setSaveError(
          err instanceof Error ? err.message : "Couldn't clear this role. Try again.",
        );
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85dvh] flex-col overflow-hidden sm:max-w-2xl">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <IdentificationBadgeIcon size={18} weight="regular" /> Role for this conversation
          </DialogTitle>
          <DialogDescription>
            Use this space to tell EDDY what role to take on here and how to work. Everything you
            write is added to EDDY&rsquo;s context for this conversation — every reply in this
            chat follows it, and a voice call started from this chat opens already in the role.
            Your other chats are unaffected.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {isError ? (
            // FRONT-1525: a failed query has isPending===false AND
            // data===undefined — this branch keys off `isError` on its own
            // rather than an absence check, so a genuine load failure gets
            // its own visible message instead of spinning forever.
            <div className="flex flex-1 items-center justify-center px-2 text-center text-sm text-destructive">
              Couldn&rsquo;t load this conversation&rsquo;s role.{' '}
              {error instanceof Error ? error.message : 'Try again.'}
            </div>
          ) : !loaded ? (
            <div className="flex flex-1 items-center justify-center text-muted-foreground">
              <CircleNotchIcon size={18} className="animate-spin" />
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <Textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={PLACEHOLDER}
                aria-label="Role instructions"
                disabled={saving}
                className="min-h-[320px] flex-1 resize-none text-sm"
              />
              {recording ? (
                <RecordingBar
                  elapsedMs={recordingMs}
                  onCancel={voice.cancel}
                  onFinish={voice.stopAndTranscribe}
                />
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Tip label={transcribing ? 'Transcribing…' : 'Dictate with your microphone'}>
                      <button
                        type="button"
                        onClick={voice.start}
                        disabled={transcribing || saving}
                        aria-label="Dictate role instructions"
                        className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                      >
                        {transcribing ? (
                          <CircleNotchIcon size={14} className="animate-spin" />
                        ) : (
                          <MicrophoneIcon size={14} weight="regular" />
                        )}
                      </button>
                    </Tip>
                    <span>Type it, or dictate it with the microphone.</span>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 text-xs tabular-nums',
                      overCap ? 'text-destructive' : 'text-muted-foreground',
                    )}
                  >
                    {draft.length} / {CHAT_ROLE_MAX_LENGTH}
                  </span>
                </div>
              )}
              {saveError && (
                <div className="text-xs text-destructive" role="alert">
                  {saveError}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 sm:justify-between">
          {hasRole ? (
            <Button
              type="button"
              variant="ghost"
              onClick={handleClear}
              disabled={saving || !loaded}
              className="text-muted-foreground hover:text-destructive"
            >
              Clear role
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!loaded || !isDirty || overCap || saving}
            >
              {saving && <CircleNotchIcon size={14} className="animate-spin" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
